import { Dispatch, Reducer } from 'react';
import {
    AccessibleParkingPermitFormState,
    AddressChangeEligibilityQuestions,
    AddressChangeResp,
    HealthCardFormState,
    MTOAddProductFormState,
    PhotoCardFormState,
    ValidationResponse,
} from '~/components/address-change/type';
import { AlertType } from '~/components/common/page-alerts';
import { Address } from '~/components/driver-record-3-year/types';
import { AdminMessage } from '~/models/admin';
import { InboxMode, Message } from '~/models/inbox/inbox';
import { RenewalEligibilityData, TransactionCategory } from '~/models/product-renewal';
import Profile, { PersonalDetails } from '~/models/profile';
import { RenewalReminder, ServiceType } from '~/models/renewal-reminders';
import { DriversLicenceType, PhotoCardType } from '~/components/products/types';

export interface DraftForm {
    id: string;
    title: string;
    formId?: string;
    description?: string;
    serviceName?: string;
    createdAt?: string;
    updatedAt?: string;
    expiresOn?: string;
    resumeUrl?: string;
}

export interface EhocmDraftFormValue {
    incidentDetails?: string;
    incidentDateTime?: string;
    incidentLocation?: string;
    stepperIndex?: number;
    [key: string]: unknown;
}

export interface DraftReviewField {
    label: string;
    labelFRA: string;
    value?: string;
    valueFRA?: string;
}

export interface DraftReviewSection {
    title: string;
    titleFRA: string;
    editLabel: string;
    editLabelFRA: string;
    fields: DraftReviewField[];
    sectionIndex?: number;
}

export interface DraftFormValue {
    sections: DraftReviewSection[];
    stepperIndex?: number;
    totalSteps?: number;
    [key: string]: unknown;
}

export interface DraftFormWithDetails extends DraftForm {
    formValue?: EhocmDraftFormValue;
    reviewFormValue?: DraftFormValue;
    completedSteps?: number;
    totalSteps?: number;
}

export interface AIPhotoData {
    photoData: {
        productDetails: {
            postalCode: string | null;
            documentNumber: string | null;
        };
        accountInfo: {
            firstName: string;
            lastName: string;
            middleName?: string | null;
        };
        addressInfo: {
            addressLine1: string | null;
            addressLine2?: string | null;
            city: string | null;
            streetSuffix: string | null;
            province: string | null;
        };
        dateOfExpiration: string | null;
    };
}

export interface PhotoCardPayload {
    type: 'PhotoCard';
    healthCardNumber: string;
    healthCardVersionCode: string;
    healthCardDocumentControlNumber: string;
    lastPostalCode: string;
}

export interface RedWhiteCardPayload {
    type: 'RedAndWhiteCard';
    healthCardNumber: string;
    healthCardVersionCode: string;
    postalCode: string;
}

export interface DriversLicensePayload {
    type: 'DriverLicense';
    driversLicenseNumber: string;
    driversLicenseIssueDateYear: number;
    driversLicenseIssueDateMonth: number;
    driversLicenseIssueDateDay: number;
    lastPostalCode: string;
}

export interface VehiclePermitPayload {
    type: 'RIN';
    licensePlateNumber?: string;
    rin: string;
    postalCode: string;
}

export interface TPCAddressInfo {
    routeType?: string;
    address2?: string;
    address1?: string;
    poBoxKeyword?: string;
    poBoxNumber?: string;
    poBoxFromNumber?: string;
    poBoxToNumber?: string;
    postalCode?: string;
    streetFromSuffix?: string;
    streetToSuffix?: string;
    suiteFromNumber?: string;
    suiteToNumber?: string;
    streetName?: string;
    streetType?: string;
    streetNumber?: string;
    streetNumberSuffix?: string;
    streetDirection?: string;
    routeNumber?: string;
    addressLine1?: string;
    municipality?: string;
    province?: string;
    streetFromNumber?: string;
    streetToNumber?: string;
    stationType?: string;
    stationName?: string;
    typeOfAddress?: number;
    directoryArea?: string;
    suiteNumber?: string;
    deliveryInstallationType?: string;
    deliveryInstallationName?: string;
    deliveryInstallationArea?: string;
    unitType?: string;
    unitNumber?: string;
    lotType?: string;
    lotNumber?: string;
    propertyIdentifierOption?: string;
    concessionNumber?: string;
    planNumber?: string;
    township?: string;
    noStreetName?: boolean;
    sameMailingAsResidential?: boolean;
    validationSourceTypeCode?: 'CP' | 'CL' | '';
    addressLineSearch?: string;
    addressLineMailingSearch?: string;
    sameAsResidential?: boolean;
    pcLookType?: number;
    mailAddressType?: number;
}

export interface ContactInfo {
    firstName?: string;
    lastName?: string;
    middleName?: string;
    singleName?: string;
    email?: string;
    emailConfirm?: string;
    primaryPhone?: string;
    primaryExtension?: string;
    primaryPhoneNumber?: string;
    primaryExtensionNumber?: string;
    primaryPhoneExtensionNumber?: string;
    alternativePhone?: string;
    alternativeExtension?: string;
    mononym?: string;
}

export interface ContactInfoIacApi {
    individualName: { firstName: string; lastName: string };
    telephoneNumber: { areaCode: string; telephoneNumber: string; extensionNumber: string };
    alternativeTelephoneNumber: {
        areaCode: string;
        telephoneNumber: string;
        extensionNumber: string;
    };
    emailAddress: { userName: string; domainName: string };
}

export interface AddressChange {
    transactionID?: string;
    healthCard?: HealthCardFormState;
    vehiclePermit?: MTOAddProductFormState;
    residentialAddress: TPCAddressInfo;
    mailingAddress: TPCAddressInfo;
    sameAsResidential: boolean;
    mailingSameAsResidential?: boolean;
    hasFalseInfoConsent: boolean;
    isNorthernResident: string;
    agreedTermsAndConditions: boolean;
    contactInfo?: ContactInfo;
    cards?: Array<
        PhotoCardPayload | RedWhiteCardPayload | DriversLicensePayload | VehiclePermitPayload
    >;
    validationResult?: ValidationResponse;
    accessibleParkingPermit?: AccessibleParkingPermitFormState;
    ontarioPhotoCard?: PhotoCardFormState;
    addressChangeResp?: AddressChangeResp;
    selectedAddress?: TPCAddressInfo;
    selectedMailingAddress?: TPCAddressInfo;
    eligibilityQuestions?: AddressChangeEligibilityQuestions;

    productTypes: ProductTypeCode[];
}

export interface DriversAbstract {
    identificationMethod?: string;
    driversLicenseNumber?: string;
    includePersonalDriversLicenseNumber?: boolean;
    rin?: string;
    nameType?: string;
    firstName?: string;
    lastName?: string;
    middleName?: string;
    singleName?: string;
    companyName?: string;
    mononym?: string;
    country?: string;
    address?: Address;
    usAddress?: Address;
    localAddress?: TPCAddressInfo;
    addressOnProfile?: string;
    records?: { value: string }[];
    contact?: ContactInfo;
    orderId?: string;
    clientDetails?: {
        clientID?: string;
        clientName?: string;
    };
}
export interface VehicleAbstractUncertified {
    identificationMethod?: string;
    abstractType?: string;
    driversLicenseNumber?: string;
    date?: string;
    dateDay?: string;
    dateMonth?: string;
    dateYear?: string;
    includePersonalDriversLicenseNumber?: boolean;
    licensePlate?: string;
    vin?: string;
    rin?: string;
    nameType?: string;
    firstName?: string;
    lastName?: string;
    middleName?: string;
    singleName?: string;
    companyName?: string;
    mononym?: string;
    country?: string;
    address?: Address;
    usAddress?: Address;
    localAddress?: TPCAddressInfo;
    addressOnProfile?: string;
    records?: { value: string }[];
    contact?: ContactInfo;
    orderId?: string;
    abstracts?: VehicleAbstract[];
}

export interface VehicleAbstract {
    date?: string;
    dateDay?: string;
    dateMonth?: string;
    dateYear?: string;
    licensePlate?: string;
    vin?: string;
    abstractType?: string;
    fee?: number;
}

export type gender = 'Male' | 'Female' | 'X';

export type ProductRenewalType = 'DL' | 'HC' | 'OPC' | 'DLHC';

export enum ProductTypeCode {
    HEALTH_CARD = 'HC',
    DRIVING_LICENSE = 'DL',
    VEHICLE_PERMIT = 'VP',
    DRIVING_LICENSE_OR_VEHICLE_PERMIT = 'DLVP',
    DRIVING_LICENSE_HEALTH_CARD = 'DLHC',
    ACCESSIBLE_PARKING_PERMIT = 'APP',
    ONTARIO_PHOTO_CARD = 'OPC',
}

export enum ProductTypeEnum {
    DRIVERS_LICENSE = 'DriverLicense',
    ONTARIO_PHOTO_CARD = 'OntarioPhotoCard',
    VEHICLE_PERMIT = 'VehiclePermit',
    PHOTO_HEALTH_CARD = 'PhotoHealthCard',
    RED_WHITE_HEALTH_CARD = 'RedWhiteHealthCard',
    ACCESSIBLE_PARKING_PERMIT = 'APP',
}

export enum ServiceTypeEnum {
    OPRA = 'OPRA',
    DLRA = 'DLRA',
    HCRA = 'HCRA',
    VLRA = 'VLRA',
}

export enum PaymentStatus {
    TRANSACTION_CANCELLED_BY_USER = '100', // user clicked cancel button
    NO_PAYMENT_SERVICE = '223', // return null from payment service
    SESSION_EXPIRED = '300',
    SESSION_CLOSED = '301',
    INCORRECT_SESSION_ID = '400',

    // payment preauth success codes
    TRANSACTION_COMPLETE = '001', // transaction successful, both update Order and upload file success
    UPLOAD_DOCUMENTS_FAILED = '011', // update Order success and upload file fail

    UPDATE_ORDER_FAILED = '002', // payment successful, update order failed. ("orderStatus": "success": false ) MTO will release later
    UPDATE_ORDER_ITEM_FAILED = '022', // update order failed. ("itemStatus": "success": false )

    TRANSACTION_CANCELLED = '003',
    CANCEL_ORDER_FAILED = '004', // MTO will release later
    TRANSACTION_REVERSED = '005',
    TRANSACTION_REVERSAL_FAILED = '006', // MTO will release later
    TRANSACTION_REVERSAL_UNKNOWN = '007', // MTO will release later
    TRANSACTION_UNKNOWN = '008',

    PAYMENT_SERVICE_NOT_AVAILABLE = 'PAYSRV0001', // Service unreachable or API time-out

    // payment preauth failed / unknown codes
    TRANSACTION_DECLINED = '010',
    TRANSACTION_UNKNOWN_AND_REVERSAL_FAILED = '012', // MTO will relase later
    TRANSACTION_UNKNOWN_AND_REVERSAL_DECLINED = '013', // MTO will relase later
    TRANSACTION_UNKNOWN_AND_REVERSED = '014', // MTO will relase later
    TRANSACTION_UNKNOWN_AND_REVERSAL_UNKNOWN = '015', // MTO will relase later

    TRANSACTION_ERROR = '020',
    TRANSACTION_ERROR_SE = '020SE',
    TRANSACTION_ERROR_RE = '020RE',
}

export interface HealthCardDetails {
    healthCardNumberWithVersionCode: string;
    documentControlNumber: string;
}

export interface DriverLicenseDetails {
    driversLicenceNumber: string;
    trilliumNumber: string;
    postalCode: string;
}

export interface OntarioPhotoCardDetails {
    photoCardNumber: string;
    ddNumber: string;
    postalCode: string;
}

export interface ProductRenewalOrderStatus {
    healthCard?: {
        success: boolean;
        successToken: string;
        transactionId: string;
        cxpTransactionId: string;
        messageCode: string;
        cxpErrorCode: string;
        messageCodeDescription: string;
    };
    driversLicence?: {
        cxpTransactionId?: string;
        success?: boolean;
        tdlOrConfirm?: string;
        isTDLOrConfirmUploaded?: boolean;
    };
    photoCard?: {
        cxpTransactionId?: string;
        success?: boolean;
    };
    payment?: {
        transactionId?: string;
        amount?: number;
        attempts?: number; // this will be 0 or undefined in the initial state, every subsequent attempts (after the initial attempt should increment this counter
        statusCode?: PaymentStatus;
    };
}

export interface ProductRenewalState {
    renewalType?: ProductRenewalType;
    isVerificationRequired?: boolean;
    suppressIneligibleBanner?: boolean;
    skippedRenewalOptionsPage?: boolean;
    agreedTermsAndConditions?: boolean;
    eligibilityQuestions?: {
        requireGlassesOrLenses?: boolean;
        hasMedicalConditions?: boolean;
        addressChangedInLast90Days?: boolean;
        currentlyLiveInAddressListed?: boolean;
        livedInOntarioForMinimumDuration?: boolean;
    };
    driversLicenceDetails?: DriverLicenseDetails;
    photoCardDetails?: OntarioPhotoCardDetails;
    healthCardDetails?: HealthCardDetails;
    contactDetails?: {
        emailId?: string;
        primaryPhoneNumber: string;
        primaryPhoneExtensionNumber?: string;
    };
    isOntarioResident?: string;
    certifyRenewalInfo?: boolean;
    confirmVisionChange?: boolean;
    eligibilityData?: RenewalEligibilityData;
    orderStatus?: ProductRenewalOrderStatus;
    transactionCategory?: TransactionCategory;
}

export interface Inbox {
    mode: InboxMode;
    messages?: Message[];
    numOfMessages: number;
    numOfStarredMessages: number;
    numOfUnreadMessages: number;
    pageSize: number;
    pageNumber: number;
    inboxError?: boolean;
}

export type RenewalTerm = '1' | '2';

export interface LicensePlateDetail {
    licensePlateNumber?: string;
    permitNumber?: string;
    odometerReading?: string;
    insuranceCompany?: string;
    policyNumber?: string;
    renewalTerm?: RenewalTerm;
    inquireResponse?: any;
}

export interface LicensePlateDetailUpdate extends LicensePlateDetail {
    newLicensePlateNumber?: string;
    newPermitNumber?: string;
    newDetailResp: LicensePlateDetailResp;
}

export interface LicensePlateDetailResp extends LicensePlateDetail {
    inquireResponse?: object;
    requireInsurance?: boolean;
    requireOdometer?: boolean;
    requireTerms?: boolean;
}

export interface LicensePlateRenewal {
    acceptedNOC?: boolean;
    renewalType?: string;
    details?: LicensePlateDetail[];
    hasPrivacyConsent?: boolean;
    hasCertificationConsent?: boolean;
    requireInsurance?: boolean;
    requireOdometer?: boolean;
    requireTerms?: boolean;
    contact?: ContactInfo;
    emailSent?: boolean;
    orderStatus?: {
        cxpTransactionId?: string;
        success?: boolean;
        amount?: number;
        payment?: {
            transactionId?: string;
            attempts?: number;
            statusCode?: PaymentStatus;
        };
        items?: {
            licensePlateNumber: string;
            messageCode?: string;
            success: boolean;
        }[];
    };
}

export interface CreateAccountState {
    accountInfo: PersonalDetails;
}
export interface CallerInfoState {
    verifiedBy?: string;
    callerUrl?: string;
}

export type RenewalFlow = 'DLV' | 'OPCV' | 'HCV' | 'DLHCV' | 'IACV' | '';

export interface verifyProductInfoType {
    binding?: 'yes' | 'no' | '';
    email: string;
}

export interface StoreStateProps {
    intendedFlow: string;
    renewalFlow: RenewalFlow;
    languagePref: string;
    isReminderActive: boolean;
    isReferredByDashboard?: boolean;
    isLoading: boolean;
    errors?: string[];
    profileErrors?: string[];
    admin?: {
        isLoading?: boolean;
        error?: string;
        messages?: AdminMessage[];
    };
    messages?: {
        type: AlertType;
        title?: string;
        message: string; // Required
        isTranslationKey: boolean; // Required - If true, title and message are i18n keys. PREFER true for language support.
    }[];
    customer?: Profile;
    draftsAndForms?: DraftForm[];
    renewalReminder?: RenewalReminder;
    registeredProducts: ServiceType[];
    addressChange: Partial<AddressChange>;
    aiPhotoData?: AIPhotoData;
    inbox: Inbox;
    callerInfo?: CallerInfoState;
    productRenewal?: ProductRenewalState;
    licensePlateRenewal?: LicensePlateRenewal;
    createAccount?: CreateAccountState;
    payFine?: PayFine;
    payFineGuest?: PayFineGuest;
    selectedFinesObj?: PayFine;
    selectedFinesValue?: boolean;
    checkAmp?: string[];
    checkReinst?: string[];
    checkDriversFine?: string[];
    checknsf?: string[];
    cancelOrderSuccessMessage?: boolean;
    declineOrderStatus?: boolean;
    payFineAttempts?: number;
    paymentOrderDate: OrderDateObj;
    driversAbstract?: Partial<DriversAbstract>;
    vehicleAbstractUncertified?: Partial<VehicleAbstractUncertified>;
    products?: {
        driversLicence?: DriversLicenceType;
        photoCard?: PhotoCardType;
    };
    payFineInitBy?: PayFineFlowInitBy;
    verifyProductInfo?: verifyProductInfoType;
}

export interface PayloadOptions {
    intendedFlow?: string;
    renewalFlow?: RenewalFlow;
    isLoading?: boolean;
    languagePref?: string;
    isReferredByDashboard?: boolean;
    isReminderActive?: boolean;
    errors?: string[];
    profileErrors?: string[];
    admin?: Partial<{
        isLoading: boolean;
        error?: string;
        messages?: AdminMessage[];
    }>;
    messages?: {
        type: AlertType;
        title?: string;
        message: string; // Required
        isTranslationKey: boolean; // Required - If true, title and message are i18n keys. PREFER true for language support.
    }[];
    customer?: Profile;
    draftsAndForms?: DraftForm[];
    renewalReminder?: RenewalReminder;
    registeredProducts?: ServiceType[];
    addressChange?: Partial<AddressChange>;
    aiPhotoData?: AIPhotoData;
    callerInfo?: Partial<CallerInfoState>;
    selectedPCAddressInfo?: {
        selectedAddress: TPCAddressInfo;
        selectedMailingAddress: TPCAddressInfo;
    };
    inbox?: Partial<Inbox>;
    productRenewal?: Partial<ProductRenewalState>;
    licensePlateRenewal?: Partial<LicensePlateRenewal>;
    createAccount?: CreateAccountState;
    payFine?: PayFine;
    payFineGuest?: PayFineGuest;
    selectedFinesObj?: PayFine;
    selectedFinesValue?: boolean;
    checkAmp?: string[];
    checkReinst?: string[];
    checknsf?: string[];
    checkDriversFine?: string[];
    cancelOrderSuccessMessage?: boolean;
    declineOrderStatus?: boolean;
    payFineAttempts?: number;
    paymentOrderDate?: OrderDateObj;
    driversAbstract?: DriversAbstract;
    vehicleAbstractUncertified?: VehicleAbstractUncertified;
    vehicleAbstract?: VehicleAbstract;
    products?: Partial<{
        driversLicence?: DriversLicenceType;
        photoCard?: PhotoCardType;
    }>;
    payFineInitBy?: PayFineFlowInitBy;
    verifyProductInfo?: verifyProductInfoType;
}
export interface OrderDateObj {
    emailId: string;
    orderId: string;
    firstName: string;
    lastName: string;
    orderStatus: string;
    partyId: number;
}

export interface ActionProps {
    type: string;
    payload?: PayloadOptions;
    status?: string;
}

export interface AppContextProps {
    state: StoreStateProps;
    dispatch: Dispatch<ActionProps>;
}

export interface AppProviderProps {
    reducer: Reducer<StoreStateProps, ActionProps>;
    initState: StoreStateProps;
}
export interface WarningMsgs {
    message: string;
    warningCode: string;
}
export type PayFineFlowInitBy = 'CXPDashboard' | 'DLDashboard';
export interface PayFine {
    messages?: [
        {
            messageType: string;
            messageNumber: string;
            messageText: string;
        },
    ];
    driverFees?: {
        feeCode: string;
        feeAmount: number;
        feeRequiredCode: string;
        productName: string;
        orderLineItemId?: number;
    }[];
    driverFines?: {
        productName: string;
        caseDetails: {
            caseNumber: string;
            caseCount: number;
            offenceCode: string;
            offenceCaseNumber: string;
            convictionDate: string;
            offenceDate: string;
            courtNumber: string;
            caseFee: number;
            courtLocation: string;
            offenceName: string;
            offenceDescription: string;
            courtLocationEnglish: string;
            courtLocationFrench: string;
            offenceNameEnglish: string;
            offenceDescriptionEnglish: string;
            offenceNameFrench: string;
            offenceDescriptionFrench: string;
            isVisible?: boolean;
            orderLineItemId?: number;
        }[];
    }[];
    ampFeesDetails?: {
        amp: string;
        productName: string;
        totalFee: number;
        ampFeesDetails: {
            suspensionNumber: string | number;
            feeCode: string;
            ampAmount: number;
            orderLineItemId?: number;
            ampTypeCode?: string;
        }[];
    }[];
    nsfFeesDetails?: {
        feeCode: string;
        feeAmount: number;
        feeRequiredCode: string;
        orderLineItemId?: number;
    }[];
    warningCodes?: WarningMsgs[];
    versionNumber?: string;
    dlNumber?: string;
    orderId?: string;
    orderGUID?: string;
    clientId?: string;
    clientName?: string;
    sessionId?: string;
    din?: string;
    businessTransactionNumber?: string;
}
export interface ContactDetails {
    isSameAsDL?: boolean;
    lastName?: String;
    firstName?: String;
    middleName?: String;
}
export interface PayFineGuest {
    originalDrivingLicense?: string;
    driverLicenseNumber?: string;
    driverLicenseClass?: string;
    visionCondition?: string;
    fullName?: string;
    firstName?: string;
    lastName?: string;
    middleName?: string;
    singleName?: string;
    nameCheckbox?: boolean;
    DOB?: string;
    email?: string;
    confirmationEmail?: string;
    confirmEmail?: boolean;
    phoneNumber?: string;
    extension?: string;
    streetAddress?: string;
    municipality?: string;
    postalCode?: string;
    offenceNumber?: string;
    suspensionNumber?: string;
    language?: string;
    contactDetails?: ContactDetails;
}
export interface PayFineCancelOrder {
    orderId: string;
    orderCancellationReason: string;
    orderCancellationDate: Date;
    statusCode: string;
}
